/**
 * Grouping utilities for multi-level row grouping
 * Story 4.1: Implement Multi-Level Grouping
 * Story 4.2: Implement Time Slicer (date field grouping by time periods)
 * AC-4.1, AC-4.2, AC-4.3, AC-4.4
 */

import type { GridRow, GridColumn, RowAggregationMode } from '../types/gridTypes';
import type { GroupingConfig, GroupingField } from '../../../types/types/view';
import { getTimePeriodKey, getTimePeriodLabel, isDateColumnType } from './timeSlicer';

// =============================================================================
// CONSTANTS
// =============================================================================

/** Maximum supported grouping levels (AC-4.4) */
export const MAX_GROUPING_LEVELS = 5;

/** Prefix for generated group row IDs */
const GROUP_ID_PREFIX = 'group';

// =============================================================================
// GROUP ROW ID UTILITIES
// =============================================================================

/**
 * Generate a stable, URL-safe group row ID
 * Uses encodeURIComponent to handle special characters in values
 *
 * @param level - Nesting level (0-based)
 * @param fieldId - Field ID being grouped
 * @param value - Group value (will be encoded)
 * @returns Predictable group ID for state persistence
 */
export function generateGroupRowId(
  level: number,
  fieldId: string,
  value: unknown
): string {
  const encodedValue = encodeURIComponent(
    String(value ?? 'null').slice(0, 50)
  );
  return `${GROUP_ID_PREFIX}-${level}-${fieldId}-${encodedValue}`;
}

/**
 * Parse a group row ID to extract level, fieldId, and value
 *
 * @param groupId - Group row ID to parse
 * @returns Parsed components or null if not a valid group ID
 */
export function parseGroupRowId(
  groupId: string
): { level: number; fieldId: string; value: string } | null {
  const match = groupId.match(/^group-(\d+)-([^-]+)-(.+)$/);
  if (!match) return null;

  return {
    level: parseInt(match[1], 10),
    fieldId: match[2],
    value: decodeURIComponent(match[3]),
  };
}

/**
 * Check if a row ID is a generated group row
 */
export function isGroupRow(rowId: string): boolean {
  return rowId.startsWith(`${GROUP_ID_PREFIX}-`);
}

/**
 * Get the group level from a row (returns -1 for non-group rows)
 */
export function getGroupLevel(row: GridRow): number {
  if (row.rowType !== 'group') return -1;
  const parsed = parseGroupRowId(row.id);
  return parsed?.level ?? -1;
}

// =============================================================================
// TIME SLICER TRANSFORM (Story 4.2)
// =============================================================================

/**
 * Period label cache for display purposes
 * Maps fieldId -> periodKey -> displayLabel
 */
type PeriodLabels = Map<string, Map<string, string>>;

/**
 * Transform date values to period keys for time-sliced grouping
 * Called before sorting when a grouping field has timeSlicer enabled
 *
 * Story 4.2: Implement Time Slicer
 * AC-4.2.2, AC-4.2.3: Group by week/month periods
 * AC-4.2.4: Locale-aware labels
 *
 * @param rows - Data rows to transform
 * @param groupingFields - Grouping configuration with time slicer settings
 * @param columns - Column definitions for type detection
 * @returns Transformed rows and period label lookup
 */
function applyTimeSlicerTransform(
  rows: GridRow[],
  groupingFields: GroupingField[],
  columns: GridColumn[]
): { rows: GridRow[]; periodLabels: PeriodLabels } {
  const periodLabels: PeriodLabels = new Map();

  // Check which fields have time slicer enabled
  const timeSlicedFields = groupingFields.filter(f => f.timeSlicer?.enabled);
  if (timeSlicedFields.length === 0) {
    return { rows, periodLabels };
  }

  // Build column type lookup
  const columnTypes = new Map(columns.map(c => [c.id, c.type]));

  // Transform rows
  const transformedRows = rows.map(row => {
    const newValues = { ...row.values };

    for (const field of timeSlicedFields) {
      const colType = columnTypes.get(field.fieldId);
      if (!colType || !isDateColumnType(colType)) continue;

      const originalValue = row.values[field.fieldId];
      const period = field.timeSlicer!.period;
      const weekStartsOn = field.timeSlicer!.weekStartsOn ?? 0;

      const periodKey = getTimePeriodKey(originalValue as Date | string | null, period, weekStartsOn);
      const periodLabel = getTimePeriodLabel(periodKey, period, weekStartsOn);

      // Store period key as the grouping value
      newValues[field.fieldId] = periodKey;

      // Track labels for display
      if (!periodLabels.has(field.fieldId)) {
        periodLabels.set(field.fieldId, new Map());
      }
      periodLabels.get(field.fieldId)!.set(periodKey, periodLabel);
    }

    return { ...row, values: newValues };
  });

  return { rows: transformedRows, periodLabels };
}

// =============================================================================
// CORE GROUPING FUNCTIONS
// =============================================================================

/**
 * Build grouped rows from flat rows based on grouping configuration
 *
 * Algorithm:
 * 1. Sort rows by grouping field values (A, then B within A, etc.)
 * 2. Iterate through sorted rows
 * 3. For each unique value combination, generate group header row
 * 4. Set parentId relationships for hierarchy
 * 5. If hideEmptyGroups, filter out groups with no child rows
 *
 * @param rows - Flat array of data rows
 * @param columns - Column definitions (for aggregation context)
 * @param groupingConfig - Grouping configuration
 * @returns Grouped rows with headers inserted
 */
export function buildGroupedRows(
  rows: GridRow[],
  columns: GridColumn[],
  groupingConfig: GroupingConfig
): GridRow[] {
  if (!groupingConfig.enabled || groupingConfig.fields.length === 0) {
    return rows;
  }

  // Limit to max levels (AC-4.4)
  const groupingFields = groupingConfig.fields.slice(0, MAX_GROUPING_LEVELS);

  // Filter out existing group/subtotal/total rows to only work with data rows
  const dataRows = rows.filter(
    r => !['group', 'subtotal', 'total'].includes(r.rowType || '')
  );

  if (dataRows.length === 0) {
    return [];
  }

  // Apply time slicer transform if any date fields have it enabled (Story 4.2)
  const { rows: transformedRows, periodLabels } = applyTimeSlicerTransform(
    dataRows,
    groupingFields,
    columns
  );

  // Sort rows by grouping field values (period keys sort alphabetically/chronologically)
  const sortedRows = sortRowsByGroupingFields(transformedRows, groupingFields);

  // Build grouped structure
  const result: GridRow[] = [];

  // Track current group values at each level
  const currentGroupValues: (unknown | undefined)[] = new Array<unknown | undefined>(groupingFields.length).fill(undefined);
  const currentGroupIds: (string | null)[] = new Array<string | null>(groupingFields.length).fill(null);

  for (const row of sortedRows) {
    // Determine which group levels need new headers
    let needNewHeaderFrom = -1;

    for (let level = 0; level < groupingFields.length; level++) {
      const fieldId = groupingFields[level].fieldId;
      const value = row.values[fieldId];

      if (currentGroupValues[level] !== value) {
        needNewHeaderFrom = level;
        break;
      }
    }

    // Create new group headers if needed
    if (needNewHeaderFrom >= 0) {
      for (let level = needNewHeaderFrom; level < groupingFields.length; level++) {
        const fieldId = groupingFields[level].fieldId;
        const value = row.values[fieldId];

        const groupId = generateGroupRowId(level, fieldId, value);
        const parentId = level > 0 ? currentGroupIds[level - 1] : null;

        // Get display label - use period label if available (Story 4.2)
        const fieldPeriodLabels = periodLabels.get(fieldId);
        const displayLabel = fieldPeriodLabels?.get(String(value)) ?? String(value ?? '(empty)');

        const groupRow = createGroupRow(
          groupId,
          parentId,
          value,
          fieldId,
          level,
          groupingFields[level],
          columns,
          groupingConfig.defaultExpanded,
          displayLabel
        );

        result.push(groupRow);

        currentGroupValues[level] = value;
        currentGroupIds[level] = groupId;
      }
    }

    // Add data row with parentId set to innermost group
    const innermostGroupId = currentGroupIds[groupingFields.length - 1];
    const groupedRow: GridRow = {
      ...row,
      parentId: innermostGroupId,
    };
    result.push(groupedRow);
  }

  // Compute aggregations for group rows
  const aggregatedResult = computeGroupAggregations(result, columns, groupingFields);

  // Filter empty groups if needed (AC-4.6)
  if (groupingConfig.hideEmptyGroups) {
    return filterEmptyGroups(aggregatedResult);
  }

  return aggregatedResult;
}

/**
 * Sort rows by grouping field values in order
 */
function sortRowsByGroupingFields(
  rows: GridRow[],
  groupingFields: GroupingField[]
): GridRow[] {
  return [...rows].sort((a, b) => {
    for (const field of groupingFields) {
      const valueA = a.values[field.fieldId];
      const valueB = b.values[field.fieldId];

      // Handle null/undefined
      if (valueA == null && valueB == null) continue;
      if (valueA == null) return -1;
      if (valueB == null) return 1;

      // String comparison
      const strA = String(valueA);
      const strB = String(valueB);
      const cmp = strA.localeCompare(strB);
      if (cmp !== 0) return cmp;
    }
    return 0;
  });
}

/**
 * Create a group header row
 *
 * @param groupId - Unique ID for this group row
 * @param parentId - Parent group ID or null for top-level
 * @param value - Raw value being grouped (may be period key for time-sliced fields)
 * @param fieldId - Field ID being grouped
 * @param level - Nesting level (0-based)
 * @param groupingField - Grouping configuration for this field
 * @param columns - Column definitions
 * @param defaultExpanded - Whether groups are expanded by default
 * @param displayLabel - Human-readable label for display (Story 4.2: period labels for date fields)
 */
function createGroupRow(
  groupId: string,
  parentId: string | null,
  value: unknown,
  fieldId: string,
  level: number,
  groupingField: GroupingField,
  columns: GridColumn[],
  defaultExpanded: boolean,
  displayLabel?: string
): GridRow {
  // Build aggregation config from grouping field settings
  const perColumn: Record<string, RowAggregationMode> = {};

  for (const col of columns) {
    const mode = groupingField.aggregations[col.id];
    if (mode && mode !== 'none') {
      perColumn[col.id] = mode as RowAggregationMode;
    }
  }

  // Set default aggregations for numeric columns if not specified
  for (const col of columns) {
    if (!perColumn[col.id] && ['number', 'currency', 'percent'].includes(col.type)) {
      perColumn[col.id] = 'sum';
    }
  }

  return {
    id: groupId,
    parentId,
    rowType: 'group',
    expanded: defaultExpanded,
    aggregationConfig: {
      enabled: true,
      perColumn,
    },
    values: {
      // Store the group value in the grouped field (period key for time-sliced)
      [fieldId]: value,
      // Store display label - use provided label or fall back to raw value (Story 4.2)
      __groupLabel: displayLabel ?? String(value ?? '(empty)'),
      __groupLevel: level,
      __groupFieldId: fieldId,
    },
  };
}

/**
 * Compute aggregations for all group rows
 * Reuses existing aggregation logic from rowAggregation.ts
 */
function computeGroupAggregations(
  rows: GridRow[],
  columns: GridColumn[],
  _groupingFields: GroupingField[]
): GridRow[] {
  // Build parent-child map
  const childrenMap = new Map<string, string[]>();
  const rowMap = new Map(rows.map(r => [r.id, r]));

  for (const row of rows) {
    if (row.parentId) {
      if (!childrenMap.has(row.parentId)) {
        childrenMap.set(row.parentId, []);
      }
      childrenMap.get(row.parentId)!.push(row.id);
    }
  }

  // Helper to get contributing descendants (non-aggregator rows)
  const getContributingDescendants = (rowId: string): GridRow[] => {
    const result: GridRow[] = [];
    const queue = [...(childrenMap.get(rowId) || [])];

    while (queue.length > 0) {
      const currentId = queue.shift()!;
      const current = rowMap.get(currentId);
      if (!current) continue;

      // Check if this row is an aggregator
      const isAggregator =
        current.aggregationConfig?.enabled ||
        ['group', 'subtotal', 'total'].includes(current.rowType || '');

      // If it's NOT an aggregator, it contributes to the sum
      if (!isAggregator) {
        result.push(current);
      }

      // Still traverse children
      if (childrenMap.has(currentId)) {
        queue.push(...childrenMap.get(currentId)!);
      }
    }
    return result;
  };

  // Create updated rows with aggregations
  const updatedRowsMap = new Map(
    rows.map(r => [r.id, { ...r, values: { ...r.values } }])
  );

  // Process group rows
  for (const row of rows) {
    if (row.rowType !== 'group' || !row.aggregationConfig?.enabled) continue;

    const descendants = getContributingDescendants(row.id);
    const config = row.aggregationConfig;
    const rowToUpdate = updatedRowsMap.get(row.id)!;

    for (const col of columns) {
      const mode = config.perColumn?.[col.id] || 'none';
      if (mode === 'none') continue;

      const values = descendants
        .map(d => {
          const v = d.values[col.id];
          if (typeof v === 'string' && v.trim() !== '') {
            const f = parseFloat(v);
            return isNaN(f) ? null : f;
          }
          return typeof v === 'number' ? v : null;
        })
        .filter((v): v is number => v !== null);

      let result: number | null = null;

      switch (mode) {
        case 'sum':
          result = values.reduce((a, b) => a + b, 0);
          break;
        case 'avg':
          result = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
          break;
        case 'min':
          result = values.length > 0 ? Math.min(...values) : 0;
          break;
        case 'max':
          result = values.length > 0 ? Math.max(...values) : 0;
          break;
        case 'count':
          result = descendants.filter(d => {
            const v = d.values[col.id];
            return v !== null && v !== undefined && v !== '';
          }).length;
          break;
      }

      if (result !== null) {
        rowToUpdate.values[col.id] = result;
      }
    }

    // Add count of direct children as metadata
    const directChildren = childrenMap.get(row.id) || [];
    rowToUpdate.values.__childCount = directChildren.length;
  }

  return Array.from(updatedRowsMap.values());
}

/**
 * Filter out empty groups (AC-4.6)
 */
function filterEmptyGroups(
  rows: GridRow[]
): GridRow[] {
  // A group is empty if it has no data row children (only other groups)
  // We need to check recursively - a group with only empty subgroups is also empty

  const hasDataChildren = new Set<string>();

  // Build reverse lookup - child to parent
  const childToParent = new Map<string, string>();
  for (const row of rows) {
    if (row.parentId) {
      childToParent.set(row.id, row.parentId);
    }
  }

  // First pass: mark groups that have direct data children
  for (const row of rows) {
    if (row.rowType !== 'group' && row.parentId) {
      // This is a data row - mark all ancestors as having data
      let parentId: string | undefined = row.parentId;
      while (parentId) {
        hasDataChildren.add(parentId);
        parentId = childToParent.get(parentId);
      }
    }
  }

  // Filter out groups without data children
  return rows.filter(row => {
    if (row.rowType !== 'group') return true;
    return hasDataChildren.has(row.id);
  });
}

// =============================================================================
// UNGROUP UTILITIES
// =============================================================================

/**
 * Remove grouping from rows, returning flat data rows
 * AC-4.8: Removing a grouping level returns rows to flat view
 *
 * @param groupedRows - Grouped rows with headers
 * @returns Flat array of data rows with parentId cleared
 */
export function ungroupRows(groupedRows: GridRow[]): GridRow[] {
  return groupedRows
    .filter(row => row.rowType !== 'group')
    .map(row => ({
      ...row,
      parentId: null,
      // Clear grouping metadata from values
      values: Object.fromEntries(
        Object.entries(row.values).filter(
          ([key]) => !key.startsWith('__group')
        )
      ),
    }));
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all expanded group IDs from configuration
 */
export function getExpandedGroupIds(
  rows: GridRow[],
  groupingConfig: GroupingConfig
): Set<string> {
  if (groupingConfig.expandedGroupIds.length > 0) {
    return new Set(groupingConfig.expandedGroupIds);
  }

  // If defaultExpanded is true, expand all groups
  if (groupingConfig.defaultExpanded) {
    return new Set(
      rows
        .filter(r => r.rowType === 'group')
        .map(r => r.id)
    );
  }

  return new Set();
}

/**
 * Check if a row should be visible based on parent expansion state
 */
export function isRowVisible(
  row: GridRow,
  expandedGroupIds: Set<string>,
  rowMap: Map<string, GridRow>
): boolean {
  let currentParentId = row.parentId;

  while (currentParentId) {
    const parent = rowMap.get(currentParentId);
    if (!parent) break;

    // If any ancestor group is collapsed, row is not visible
    if (parent.rowType === 'group' && !expandedGroupIds.has(parent.id)) {
      return false;
    }

    currentParentId = parent.parentId;
  }

  return true;
}

/**
 * Get visible rows based on expansion state
 */
export function getVisibleRows(
  rows: GridRow[],
  expandedGroupIds: Set<string>
): GridRow[] {
  const rowMap = new Map(rows.map(r => [r.id, r]));

  return rows.filter(row => isRowVisible(row, expandedGroupIds, rowMap));
}
